'use client';

import { memo } from 'react';
import { AppImage } from '@/components/common/AppImage';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { CurrentBid } from '@/components/auction/CurrentBid';
import type { Player } from '@/lib/types/auction';
import { formatCurrency } from '@/lib/utils/format';
import { CheckCircle, SkipForward } from 'lucide-react';
import { SportSpecificDetails } from '@/components/common/SportSpecificDetails';

interface CurrentPlayerSectionProps {
  currentPlayer?: Player | null;
  status: 'waiting' | 'active' | 'paused' | 'completed';
  currentBidAmount: number;
  currentBidderTeam: string | null;
  currentBidderLogo?: string | null;
  isAdmin: boolean;
  isTeamOwner: boolean;
  onApproveSale: () => void;
  onSkipPlayer: () => void;
  tournamentId: string;
}

export const CurrentPlayerSection = memo(
  ({
    currentPlayer,
    status,
    currentBidAmount,
    currentBidderTeam,
    currentBidderLogo,
    isAdmin,
    onApproveSale,
    onSkipPlayer,
  }: CurrentPlayerSectionProps) => {
    /** ✅ Handle Empty State (After Hooks) */
    if (!currentPlayer) {
      return (
        <Card className="flex h-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-6 text-gray-500 shadow-lg sm:p-8">
          <p className="text-center text-lg font-semibold">
            {status === 'completed'
              ? 'Auction Completed'
              : 'Waiting for next player...'}
          </p>
        </Card>
      );
    }

    /** ✅ Player Details */
    const displayName = currentPlayer.user?.name || 'Unknown Player';
    const userImage = currentPlayer.user?.profilePicture || '';
    const role = currentPlayer.role || 'N/A';
    const poolName = currentPlayer.pool?.name || 'Unknown Pool';
    const basePrice = currentPlayer.basePrice;

    return (
      <Card className="relative h-full overflow-hidden rounded-xl border-none shadow-xl">
        {/* 🔹 Background Gradient */}
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-primary-500 to-accent-blue opacity-90" />

        {/* ✅ Player Info Section */}
        <div className="relative z-10 flex flex-col items-center space-y-4 p-5 sm:flex-row sm:items-center sm:space-x-6 sm:p-4">
          {/* 🔹 Player Image */}
          <div className="relative h-32 w-32 overflow-hidden rounded-full border-4 border-white shadow-md sm:h-40 sm:w-40">
            <AppImage
              src={userImage}
              alt={displayName}
              gradient
              fallbackText={displayName}
              className="object-cover"
            />
          </div>

          {/* 🔹 Player Name & Role */}
          <div className="text-center text-white sm:text-left">
            <h2 className="text-2xl font-bold tracking-wide sm:text-3xl">
              {displayName}
            </h2>
            <p className="mt-1 text-lg font-medium capitalize opacity-90">
              {role}
            </p>

            {/* 🔥 Pool & Base Price */}
            <div className="mt-2 flex flex-row items-center space-x-6">
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium opacity-80">Pool</span>
                <span className="text-lg font-semibold">{poolName}</span>
              </div>
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium opacity-80">
                  Base Price
                </span>
                <span className="text-lg font-semibold text-yellow-300">
                  {formatCurrency(basePrice)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 🔹 Current Bid Component */}
        <div className="p-2">
          <CurrentBid
            currentBidAmount={currentBidAmount}
            basePrice={basePrice}
            currentBidderTeam={currentBidderTeam}
            currentBidderLogo={currentBidderLogo}
          />
        </div>

        {/* 🔹 Sport-specific Details Section */}
        {currentPlayer.sportDetails && (
          <div className="px-4 pb-2">
            <SportSpecificDetails
              sportDetails={currentPlayer.sportDetails}
              theme="dark"
            />
          </div>
        )}

        {/* 🔹 Admin Controls */}
        {isAdmin && (
          <div className="mt-4 flex justify-center space-x-4 px-2 pb-4">
            {Number(currentBidAmount) >= Number(basePrice) &&
            currentBidderTeam ? (
              <Button
                variant="secondary"
                onClick={onApproveSale}
                className="flex-1"
                leftIcon={<CheckCircle className="h-4 w-4" />}
              >
                Approve Sale
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={onSkipPlayer}
                className="flex-1"
                leftIcon={<SkipForward className="h-4 w-4" />}
              >
                Skip Player
              </Button>
            )}
          </div>
        )}
      </Card>
    );
  }
);
